import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTint, faMountain, faEllipsis, faLock, faBuilding, faLink, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { DatePicker, AddressInput } from '../ui';
import HAMSTIRBuilderBlock from './HAMSTIRBuilderBlock';
import { crmService } from '../../services/crmService';
import {
    INDUSTRY_OPTIONS,
    PARTNER_SUBCATEGORY_OPTIONS,
    PARTNER_TYPE_OPTIONS,
} from '../../constants/crmVocabularies';
import systemDataService from '../../services/systemDataService';
import '../CRMProfileScreen.css';
import addressFromSuggestion from '../../utils/addressFromSuggestion';

const CRMPartnerProfileTab = ({ profileData, onDataChange, isNewProfile }) => {
    const history = useHistory();
    
    const [formData, setFormData] = useState({
        legal_entity_name: '',
        trading_short_name: '',
        creditor_debtor_selections: [],
        partner_type_selections: [],
        partner_subcategory_selections: [],
        industry_selections: [],
        company_website_url: '',
        head_office_address_data: {},
        bank_account_holder_address_data: [],
        invoice_address_data: {},
        incorporation_date: '',
        business_categories: [],
        business_category_other_description: '',
        is_group_entity: false,
        ...profileData
    });

    const [showPartnerTypeDropdown, setShowPartnerTypeDropdown] = useState(false);
    const [showSubcategoryDropdown, setShowSubcategoryDropdown] = useState(false);
    const [showIndustryDropdown, setShowIndustryDropdown] = useState(false);
    const partnerTypeRef = useRef(null);
    const subcategoryRef = useRef(null);
    const industryRef = useRef(null);

    // Office link suggestion state (for HMS entity auto-linking)
    const [officeSuggestion, setOfficeSuggestion] = useState(null);
    const [officeLinkLoading, setOfficeLinkLoading] = useState(false);

    // Entity linking state
    const [entityOptions, setEntityOptions] = useState([]);
    const [linkedEntityIds, setLinkedEntityIds] = useState(new Set());
    const [entityLinkLoading, setEntityLinkLoading] = useState(false);
    const [showEntityDropdown, setShowEntityDropdown] = useState(false);
    const entityDropdownRef = useRef(null);

    // Fuzzy search state for Legal Entity Name
    const [showLegalNameSuggestions, setShowLegalNameSuggestions] = useState(false);
    const [legalNameSearchResults, setLegalNameSearchResults] = useState([]);
    const [debouncedLegalName, setDebouncedLegalName] = useState('');
    const [loadingLegalNameSuggestions, setLoadingLegalNameSuggestions] = useState(false);
    const legalNameDropdownRef = useRef(null);

    // Dropdown vocabularies come from the shared constants module — the same
    // lists the KYC onboarding form renders, so a partner's tickboxes and this
    // tab can never disagree. crm/tests_vocabulary_parity.py fails on drift.
    const partnerTypeOptions = PARTNER_TYPE_OPTIONS;
    const industryOptions = INDUSTRY_OPTIONS;
    const subcategoryOptions = PARTNER_SUBCATEGORY_OPTIONS;

    useEffect(() => {
        if (profileData) {
            setFormData({
                legal_entity_name: '',
                trading_short_name: '',
                creditor_debtor_selections: [],
                partner_type_selections: [],
                partner_subcategory_selections: [],
                industry_selections: [],
                company_website_url: '',
                head_office_address_data: {},
                bank_account_holder_address_data: [],
                invoice_address_data: {},
                incorporation_date: '',
                business_categories: [],
                is_group_entity: false,
                ...profileData
            });
        }
    }, [profileData]);

    // Handle outside clicks for dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (partnerTypeRef.current && !partnerTypeRef.current.contains(event.target)) {
                setShowPartnerTypeDropdown(false);
            }
            if (subcategoryRef.current && !subcategoryRef.current.contains(event.target)) {
                setShowSubcategoryDropdown(false);
            }
            if (industryRef.current && !industryRef.current.contains(event.target)) {
                setShowIndustryDropdown(false);
            }
            if (legalNameDropdownRef.current && !legalNameDropdownRef.current.contains(event.target)) {
                setShowLegalNameSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Debounce legal entity name for fuzzy search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedLegalName(formData.legal_entity_name || '');
        }, 300);

        return () => clearTimeout(timer);
    }, [formData.legal_entity_name]);

    // Fuzzy search for legal entity name
    useEffect(() => {
        const searchProfiles = async () => {
            // Only search if we have at least 2 characters and we're creating a new profile
            if (!debouncedLegalName || debouncedLegalName.trim().length < 2 || !isNewProfile) {
                setLegalNameSearchResults([]);
                setShowLegalNameSuggestions(false);
                return;
            }

            try {
                setLoadingLegalNameSuggestions(true);
                const results = await crmService.searchProfilesByName(debouncedLegalName);
                
                // Filter out the current profile if editing (extra safety check)
                const filteredResults = profileData?.id 
                    ? results.filter(profile => profile.id !== profileData.id)
                    : results;
                
                setLegalNameSearchResults(filteredResults);
                setShowLegalNameSuggestions(filteredResults.length > 0);
            } catch (error) {
                console.error('Error searching for CRM profiles:', error);
                setLegalNameSearchResults([]);
                setShowLegalNameSuggestions(false);
            } finally {
                setLoadingLegalNameSuggestions(false);
            }
        };

        searchProfiles();
    }, [debouncedLegalName, isNewProfile, profileData?.id]);

    // Fetch entity options for linking when profile is loaded
    useEffect(() => {
        if (!profileData?.id || isNewProfile) return;
        const loadEntityOptions = async () => {
            try {
                const options = await crmService.getEntityOptions(profileData.id);
                setEntityOptions(options);
                const linked = new Set(options.filter(o => o.is_linked).map(o => o.id));
                setLinkedEntityIds(linked);
            } catch (err) {
                if (err?.response?.status !== 403) {
                    console.error('Error loading entity options:', err);
                }
            }
        };
        loadEntityOptions();
    }, [profileData?.id, isNewProfile]);

    // Close entity dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (entityDropdownRef.current && !entityDropdownRef.current.contains(event.target)) {
                setShowEntityDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleEntityToggle = async (entityId) => {
        if (!profileData?.id) return;
        const newLinked = new Set(linkedEntityIds);
        if (newLinked.has(entityId)) {
            newLinked.delete(entityId);
        } else {
            newLinked.add(entityId);
        }
        setLinkedEntityIds(newLinked);
        setEntityLinkLoading(true);
        try {
            await crmService.updateLinkedEntities(profileData.id, Array.from(newLinked));
        } catch (err) {
            console.error('Error updating linked entities:', err);
            setLinkedEntityIds(linkedEntityIds);
        } finally {
            setEntityLinkLoading(false);
        }
    };

    // Check for matching unlinked offices when is_group_entity is toggled on
    const officeSuggestionCheckedRef = useRef(false);
    useEffect(() => {
        if (!formData.is_group_entity) {
            setOfficeSuggestion(null);
            officeSuggestionCheckedRef.current = false;
            return;
        }
        if (officeSuggestionCheckedRef.current || !formData.legal_entity_name) return;
        officeSuggestionCheckedRef.current = true;

        const checkOfficeMatch = async () => {
            try {
                const offices = await systemDataService.getOffices();
                const entityName = formData.legal_entity_name.trim().toLowerCase();
                const exact = offices.find(o =>
                    !o.linked_crm_profile_id && o.name.toLowerCase() === entityName
                );
                if (exact) {
                    setOfficeSuggestion(exact);
                    return;
                }
                const partial = offices.find(o =>
                    !o.linked_crm_profile_id && (
                        o.name.toLowerCase().includes(entityName) ||
                        entityName.includes(o.name.toLowerCase())
                    )
                );
                setOfficeSuggestion(partial || null);
            } catch (err) {
                // 403 is expected for non-admin users -- silently ignore
                if (err?.response?.status !== 403) {
                    console.error('Error checking office match:', err);
                }
                setOfficeSuggestion(null);
            }
        };
        checkOfficeMatch();
    }, [formData.is_group_entity]);

    const handleLinkOffice = async () => {
        if (!officeSuggestion || !profileData?.id) return;
        setOfficeLinkLoading(true);
        try {
            await systemDataService.updateOffice(officeSuggestion.id, {
                linked_crm_profile_id: profileData.id
            });
            setOfficeSuggestion(null);
        } catch (err) {
            // 403 means user isn't an admin -- suggestion banner will just disappear
            console.error('Error linking office:', err);
            setOfficeSuggestion(null);
        } finally {
            setOfficeLinkLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        const updatedData = {
            ...formData,
            [field]: value
        };
        setFormData(updatedData);
        onDataChange(field, updatedData[field]);
    };

    const handleMultiSelectToggle = (field, option) => {
        const currentSelections = formData[field] || [];
        const updatedSelections = currentSelections.includes(option)
            ? currentSelections.filter(item => item !== option)
            : [...currentSelections, option];
        
        handleInputChange(field, updatedSelections);
    };

    const removeTag = (field, tagToRemove) => {
        const currentSelections = formData[field] || [];
        const updatedSelections = currentSelections.filter(item => item !== tagToRemove);
        handleInputChange(field, updatedSelections);
    };

    // Handle clicking on a legal name suggestion to navigate to that profile
    const handleLegalNameSuggestionClick = (profileId) => {
        history.push(`/dashboard/crm/profile/${profileId}`);
    };

    const handleAddressSelect = (field, suggestion) => {
        handleInputChange(field, addressFromSuggestion(suggestion));
    };

    const addBankAccountHolderAddress = () => {
        const currentLocations = formData.bank_account_holder_address_data || [];
        const newLocation = {
            id: Date.now(),
            formatted_address: '',
            geometry: null,
            place_name: '',
            center: null
        };
        handleInputChange('bank_account_holder_address_data', [...currentLocations, newLocation]);
    };

    const removeBankAccountHolderAddress = (locationId) => {
        const currentLocations = formData.bank_account_holder_address_data || [];
        const updatedLocations = currentLocations.filter(location => location.id !== locationId);
        handleInputChange('bank_account_holder_address_data', updatedLocations);
    };

    const handleBankAccountHolderAddressSelect = (locationId, suggestion) => {
        const currentLocations = formData.bank_account_holder_address_data || [];
        const updatedLocations = currentLocations.map(location => 
            location.id === locationId ? {
                ...location,
                ...addressFromSuggestion(suggestion),
            } : location
        );
        handleInputChange('bank_account_holder_address_data', updatedLocations);
    };

    const handleBankAccountHolderAddressChange = (locationId, value) => {
        const currentLocations = formData.bank_account_holder_address_data || [];
        const updatedLocations = currentLocations.map(location => 
            location.id === locationId ? {
                ...location,
                formatted_address: value
            } : location
        );
        handleInputChange('bank_account_holder_address_data', updatedLocations);
    };

    // Date formatting functions are no longer needed as DatePicker handles this internally

    return (
        <div className="crm-tab-panel">
            <div className="crm-form-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '2px solid #e5e7eb' }}>
                    <h2 className="crm-section-title" style={{ marginBottom: 0, paddingBottom: 0, border: 'none' }}>Partner Profile Information</h2>
                    <div className="crm-checkbox-item" style={{ marginBottom: 0, padding: '0.5rem 1rem', background: '#f8f9ff', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                        <input
                            type="checkbox"
                            id="hms-entity-checkbox"
                            className="crm-checkbox"
                            checked={formData.is_group_entity || false}
                            onChange={(e) => handleInputChange('is_group_entity', e.target.checked)}
                        />
                        <label htmlFor="hms-entity-checkbox" className="crm-checkbox-label">
                            Legal Entity
                        </label>
                    </div>
                </div>

                {formData.is_group_entity && officeSuggestion && (
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.6rem 1rem', marginBottom: '0.75rem',
                        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px',
                        fontSize: '0.85rem', color: '#1e40af'
                    }}>
                        <span>
                            <i className="fas fa-info-circle" style={{ marginRight: '0.4rem' }}></i>
                            Office "<strong>{officeSuggestion.name}</strong>" is not linked to any CRM profile.
                        </span>
                        {profileData?.id ? (
                            <button
                                type="button"
                                onClick={handleLinkOffice}
                                disabled={officeLinkLoading}
                                style={{
                                    padding: '0.3rem 0.8rem', marginLeft: '0.75rem',
                                    background: '#2563eb', color: '#fff', border: 'none',
                                    borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem',
                                    fontWeight: '500', whiteSpace: 'nowrap',
                                    opacity: officeLinkLoading ? 0.6 : 1
                                }}
                            >
                                {officeLinkLoading ? 'Linking...' : 'Link Office'}
                            </button>
                        ) : (
                            <span style={{ marginLeft: '0.75rem', fontSize: '0.78rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                                Save profile first to link
                            </span>
                        )}
                    </div>
                )}
                
                {/* Business Categories Selector */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
                    <div className="crm-business-categories-container">
                    <h3 className="crm-business-categories-title">
                        Business Categories
                    </h3>
                    <div className="crm-business-categories-options">
                        {/* Fuels */}
                        <div 
                            className={`crm-business-category-item ${formData.business_categories?.includes('Fuels') ? 'selected-fuels' : ''}`}
                            onClick={() => {
                                const currentCategories = formData.business_categories || [];
                                const updatedCategories = currentCategories.includes('Fuels')
                                    ? currentCategories.filter(cat => cat !== 'Fuels')
                                    : [...currentCategories, 'Fuels'];
                                handleInputChange('business_categories', updatedCategories);
                            }}
                        >
                            <FontAwesomeIcon icon={faTint} className="crm-business-category-icon fuels" />
                            <span className="crm-business-category-label">Fuels</span>
                        </div>

                        {/* Solids */}
                        <div 
                            className={`crm-business-category-item ${formData.business_categories?.includes('Solids') ? 'selected-solids' : ''}`}
                            onClick={() => {
                                const currentCategories = formData.business_categories || [];
                                const updatedCategories = currentCategories.includes('Solids')
                                    ? currentCategories.filter(cat => cat !== 'Solids')
                                    : [...currentCategories, 'Solids'];
                                handleInputChange('business_categories', updatedCategories);
                            }}
                        >
                            <FontAwesomeIcon icon={faMountain} className="crm-business-category-icon solids" />
                            <span className="crm-business-category-label">Solids</span>
                        </div>

                        {/* Other */}
                        <div
                            className={`crm-business-category-item ${formData.business_categories?.includes('Other') ? 'selected-other' : ''}`}
                            onClick={() => {
                                const currentCategories = formData.business_categories || [];
                                const isSelected = currentCategories.includes('Other');
                                const updatedCategories = isSelected
                                    ? currentCategories.filter(cat => cat !== 'Other')
                                    : [...currentCategories, 'Other'];
                                handleInputChange('business_categories', updatedCategories);
                                if (isSelected) {
                                    handleInputChange('business_category_other_description', '');
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faEllipsis} className="crm-business-category-icon other" />
                            <span className="crm-business-category-label">Other</span>
                        </div>
                    </div>
                    {formData.business_categories?.includes('Other') && (
                        <input
                            type="text"
                            className="crm-business-category-other-description"
                            value={formData.business_category_other_description || ''}
                            onChange={(e) => handleInputChange('business_category_other_description', e.target.value)}
                            placeholder="Describe the other business category..."
                            maxLength={255}
                        />
                    )}
                </div>
                </div>
                
                {/* Entity Access Linking */}
                {!isNewProfile && profileData?.id && entityOptions.length > 0 && (
                    <div style={{
                        background: '#f8f9ff',
                        border: '1px solid #e0e4f0',
                        borderRadius: '10px',
                        padding: '0.75rem 1rem',
                        marginBottom: '1rem'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: linkedEntityIds.size > 0 ? '0.5rem' : 0
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <FontAwesomeIcon icon={faBuilding} style={{ color: '#4472C4', fontSize: '0.85rem' }} />
                                <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>
                                    Entity Access
                                </span>
                                {entityLinkLoading && (
                                    <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#94a3b8', fontSize: '0.75rem' }} />
                                )}
                            </div>
                            <div style={{ position: 'relative' }} ref={entityDropdownRef}>
                                <button
                                    type="button"
                                    onClick={() => setShowEntityDropdown(!showEntityDropdown)}
                                    style={{
                                        padding: '0.3rem 0.7rem',
                                        fontSize: '0.78rem',
                                        fontWeight: 500,
                                        background: '#4472C4',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.35rem'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faLink} style={{ fontSize: '0.7rem' }} />
                                    Link Entities
                                </button>
                                {showEntityDropdown && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: 0,
                                        marginTop: '0.25rem',
                                        background: '#fff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                        zIndex: 1000,
                                        minWidth: '260px',
                                        maxHeight: '280px',
                                        overflowY: 'auto'
                                    }}>
                                        <div style={{
                                            padding: '0.5rem 0.75rem',
                                            borderBottom: '1px solid #f3f4f6',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.03em'
                                        }}>
                                            Corporate Entities
                                        </div>
                                        {entityOptions.map(entity => (
                                            <label
                                                key={entity.id}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    padding: '0.5rem 0.75rem',
                                                    cursor: 'pointer',
                                                    fontSize: '0.82rem',
                                                    borderBottom: '1px solid #f9fafb',
                                                    transition: 'background 0.15s'
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f8f9ff'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={linkedEntityIds.has(entity.id)}
                                                    onChange={() => handleEntityToggle(entity.id)}
                                                    style={{ accentColor: '#4472C4' }}
                                                />
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontWeight: 500, color: '#1f2937' }}>{entity.entity_name}</span>
                                                    <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{entity.group_name}</span>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        {linkedEntityIds.size > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                                {entityOptions
                                    .filter(e => linkedEntityIds.has(e.id))
                                    .map(entity => (
                                        <span
                                            key={entity.id}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.3rem',
                                                padding: '0.2rem 0.55rem',
                                                background: '#e8edfa',
                                                color: '#3b5998',
                                                borderRadius: '999px',
                                                fontSize: '0.75rem',
                                                fontWeight: 500
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faBuilding} style={{ fontSize: '0.6rem' }} />
                                            {entity.entity_name}
                                            <button
                                                type="button"
                                                onClick={() => handleEntityToggle(entity.id)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '0 0.15rem',
                                                    color: '#6b7fa8',
                                                    fontSize: '0.7rem',
                                                    lineHeight: 1,
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                                title={`Unlink ${entity.entity_name}`}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        </span>
                                    ))
                                }
                            </div>
                        )}
                        {linkedEntityIds.size === 0 && (
                            <div style={{
                                fontSize: '0.75rem',
                                color: '#9ca3af',
                                fontStyle: 'italic',
                                marginTop: '0.25rem'
                            }}>
                                No entities linked -- partner is visible to all CRM users
                            </div>
                        )}
                    </div>
                )}

                {/* Partner Overview Summary Block - moved from HAMSTIRBuilderBlock */}
                {formData.partner_overview_summary && (
                    <div style={{
                        background: 'linear-gradient(135deg, #f8fdf8 0%, #e8f5e8 100%)',
                        border: '2px solid #22c55e',
                        borderRadius: '12px',
                        padding: '0.75rem',
                        marginBottom: '0.75rem'
                    }}>
                        <h3 style={{ 
                            color: '#16a34a', 
                            marginBottom: '0.5rem',
                            fontSize: '1rem',
                            fontWeight: '600'
                        }}>
                            Partner Overview Summary
                        </h3>
                        <div 
                            style={{
                                backgroundColor: '#ffffff',
                                padding: '0.625rem',
                                borderRadius: '8px',
                                border: '1px solid #d1fae5',
                                fontSize: '0.875rem',
                                lineHeight: '1.4',
                                color: '#374151'
                            }}
                            dangerouslySetInnerHTML={{
                                __html: formData.partner_overview_summary
                                    .replace(/## (.+)/g, '<h4 style="color: #16a34a; font-weight: 600; margin: 0.5rem 0 0.25rem 0; font-size: 1rem;">$1</h4>')
                                    .replace(/### (.+)/g, '<h5 style="color: #059669; font-weight: 600; margin: 0.375rem 0 0.15rem 0; font-size: 0.875rem;">$1</h5>')
                                    .replace(/- \*\*(.+?):\*\* (.+)/g, '<div style="margin: 0.1rem 0; padding-left: 1rem;"><strong style="color: #374151;">$1:</strong> $2</div>')
                                    .replace(/- (.+)/g, '<div style="margin: 0.1rem 0; padding-left: 1rem;">• $1</div>')
                                    .replace(/\*\*(.+?)\*\*/g, '<strong style="color: #374151;">$1</strong>')
                                    .replace(/\n\n/g, '<div style="margin: 0.3rem 0;"></div>')
                                    .replace(/\n/g, '<br/>')
                            }}
                        />
                        {formData.partner_overview_source_urls && formData.partner_overview_source_urls.length > 0 && (
                            <div style={{ marginTop: '0.75rem', paddingTop: '0.5rem', borderTop: '1px solid #d1fae5' }}>
                                <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.025em' }}>
                                    <i className="fas fa-globe" style={{ marginRight: '0.25rem' }}></i>
                                    Sources
                                </span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginTop: '0.375rem' }}>
                                    {formData.partner_overview_source_urls.slice(0, 10).map((src, i) => (
                                        <a
                                            key={i}
                                            href={src.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={src.url}
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                                                padding: '0.2rem 0.5rem', fontSize: '0.7rem', color: '#16a34a',
                                                backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '999px',
                                                textDecoration: 'none', maxWidth: '220px', overflow: 'hidden',
                                                textOverflow: 'ellipsis', whiteSpace: 'nowrap', transition: 'all 0.15s'
                                            }}
                                            onMouseEnter={e => { e.target.style.backgroundColor = '#dcfce7'; e.target.style.borderColor = '#86efac'; }}
                                            onMouseLeave={e => { e.target.style.backgroundColor = '#f0fdf4'; e.target.style.borderColor = '#bbf7d0'; }}
                                        >
                                            <i className="fas fa-external-link-alt" style={{ fontSize: '0.6rem' }}></i>
                                            {src.title || (() => { try { return new URL(src.url).hostname; } catch { return src.url; } })()}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Basic Info Section - Visual Group */}
                <div className="crm-section-group">
                    {/* Legal Entity and Trading Names - 2 Column Grid */}
                    <div className="crm-form-grid-2">
                        <div className="crm-input-group" style={{ position: 'relative' }} ref={legalNameDropdownRef}>
                            <label className="crm-input-label required">Legal Entity Name</label>
                            <input
                                type="text"
                                className="crm-input"
                                value={formData.legal_entity_name}
                                onChange={(e) => handleInputChange('legal_entity_name', e.target.value)}
                                placeholder="Enter legal entity name"
                            />
                            
                            {/* Fuzzy Search Suggestions Dropdown */}
                            {showLegalNameSuggestions && (
                                <div className="crm-modern-multi-select-dropdown" style={{ 
                                    position: 'absolute', 
                                    top: '100%', 
                                    left: 0, 
                                    right: 0, 
                                    zIndex: 1000,
                                    marginTop: '0.25rem'
                                }}>
                                    <div style={{ 
                                        padding: '0.75rem',
                                        borderBottom: '1px solid #e5e7eb',
                                        backgroundColor: '#f9fafb',
                                        fontWeight: '600',
                                        fontSize: '0.875rem',
                                        color: '#374151'
                                    }}>
                                        <i className="fas fa-info-circle" style={{ marginRight: '0.5rem', color: '#0277BE' }}></i>
                                        Did you mean?
                                    </div>
                                    
                                    <div className="crm-modern-multi-select-options" style={{ maxHeight: '250px' }}>
                                        {loadingLegalNameSuggestions ? (
                                            <div 
                                                className="crm-modern-multi-select-option"
                                                style={{ 
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '0.25rem'
                                                }}
                                            >
                                                <span style={{ fontWeight: '600', fontStyle: 'italic', color: '#666' }}>
                                                    Searching...
                                                </span>
                                            </div>
                                        ) : (
                                            legalNameSearchResults.map((profile) => (
                                                <div 
                                                    key={profile.id} 
                                                    className="crm-modern-multi-select-option"
                                                    onClick={() => handleLegalNameSuggestionClick(profile.id)}
                                                    style={{ 
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '0.25rem'
                                                    }}
                                                >
                                                    <span style={{ fontWeight: '600' }}>{profile.legal_entity_name}</span>
                                                    {profile.trading_short_name && (
                                                        <span style={{ 
                                                            fontSize: '0.875rem', 
                                                            color: '#6b7280',
                                                            fontStyle: 'italic'
                                                        }}>
                                                            Trading as: {profile.trading_short_name}
                                                        </span>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="crm-input-group">
                            <label className="crm-input-label">Trading/Short Name</label>
                            <input
                                type="text"
                                className="crm-input"
                                value={formData.trading_short_name}
                                onChange={(e) => handleInputChange('trading_short_name', e.target.value)}
                                placeholder="Trading name"
                            />
                        </div>
                    </div>

                    {/* Creditor/Debtor Role Selection */}
                    <div className="crm-subsection-group">
                        <label className="crm-input-label">
                            Creditor/Debtor Role
                            {formData.creditor_debtor_locked && (
                                <span style={{ 
                                    marginLeft: '8px', 
                                    color: '#6b7280', 
                                    fontSize: '0.75rem',
                                    fontWeight: 'normal'
                                }}>
                                    <FontAwesomeIcon icon={faLock} style={{ marginRight: '4px' }} />
                                    Locked after approval
                                </span>
                            )}
                        </label>
                        <div className="crm-checkbox-group">
                            <div className="crm-checkbox-item">
                                <input
                                    type="checkbox"
                                    id="creditor-checkbox"
                                    className="crm-checkbox"
                                    checked={formData.creditor_debtor_selections?.includes('Creditor') || false}
                                    disabled={formData.creditor_debtor_locked}
                                    onChange={(e) => {
                                        const currentSelections = formData.creditor_debtor_selections || [];
                                        if (e.target.checked) {
                                            handleInputChange('creditor_debtor_selections', [...currentSelections, 'Creditor']);
                                        } else {
                                            handleInputChange('creditor_debtor_selections', currentSelections.filter(item => item !== 'Creditor'));
                                        }
                                    }}
                                />
                                <label 
                                    htmlFor="creditor-checkbox" 
                                    className="crm-checkbox-label"
                                    style={formData.creditor_debtor_locked ? { color: '#9ca3af', cursor: 'not-allowed' } : {}}
                                >
                                    Creditor
                                </label>
                                {formData.creditor_number && (
                                    <span style={{
                                        marginLeft: '10px',
                                        padding: '4px 10px',
                                        background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                                        color: '#ffffff',
                                        borderRadius: '6px',
                                        fontSize: '0.8rem',
                                        fontWeight: '700',
                                        letterSpacing: '0.5px',
                                        boxShadow: '0 2px 4px rgba(14, 165, 233, 0.3)'
                                    }}>
                                        {formData.creditor_number}
                                    </span>
                                )}
                            </div>
                            <div className="crm-checkbox-item">
                                <input
                                    type="checkbox"
                                    id="debtor-checkbox"
                                    className="crm-checkbox"
                                    checked={formData.creditor_debtor_selections?.includes('Debtor') || false}
                                    disabled={formData.creditor_debtor_locked}
                                    onChange={(e) => {
                                        const currentSelections = formData.creditor_debtor_selections || [];
                                        if (e.target.checked) {
                                            handleInputChange('creditor_debtor_selections', [...currentSelections, 'Debtor']);
                                        } else {
                                            handleInputChange('creditor_debtor_selections', currentSelections.filter(item => item !== 'Debtor'));
                                        }
                                    }}
                                />
                                <label 
                                    htmlFor="debtor-checkbox" 
                                    className="crm-checkbox-label"
                                    style={formData.creditor_debtor_locked ? { color: '#9ca3af', cursor: 'not-allowed' } : {}}
                                >
                                    Debtor
                                </label>
                                {formData.debtor_number && (
                                    <span style={{
                                        marginLeft: '10px',
                                        padding: '4px 10px',
                                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                        color: '#ffffff',
                                        borderRadius: '6px',
                                        fontSize: '0.8rem',
                                        fontWeight: '700',
                                        letterSpacing: '0.5px',
                                        boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)'
                                    }}>
                                        {formData.debtor_number}
                                    </span>
                                )}
                            </div>
                        </div>
                        {!formData.creditor_debtor_locked && formData.creditor_debtor_selections?.length > 0 && !isNewProfile && formData.approval_status === 'pending' && (
                            <div style={{ 
                                marginTop: '8px', 
                                fontSize: '0.75rem', 
                                color: '#6b7280',
                                fontStyle: 'italic'
                            }}>
                                Numbers will be assigned when profile is approved
                            </div>
                        )}
                    </div>
                </div>

                {/* Partner Type & Industry Multi-Select - Side by Side */}
                <div className="crm-form-grid-2">
                    <div className="crm-input-group">
                        <label className="crm-input-label required">Type of Partner</label>
                        <div className="crm-modern-multi-select-container" ref={partnerTypeRef}>
                            <div 
                                className={`crm-modern-multi-select-header ${showPartnerTypeDropdown ? 'active' : ''}`}
                                onClick={() => setShowPartnerTypeDropdown(!showPartnerTypeDropdown)}
                            >
                                <span>
                                    {formData.partner_type_selections?.length === 0 
                                        ? 'Select partner types' 
                                        : `${formData.partner_type_selections.length} selected`}
                                </span>
                            </div>
                            
                            {showPartnerTypeDropdown && (
                                <div className="crm-modern-multi-select-dropdown">
                                    <div className="crm-modern-multi-select-actions">
                                        <button 
                                            type="button" 
                                            className="crm-modern-multi-select-action-btn"
                                            onClick={() => {
                                                handleInputChange('partner_type_selections', partnerTypeOptions);
                                            }}
                                        >
                                            Select All
                                        </button>
                                        <button 
                                            type="button" 
                                            className="crm-modern-multi-select-action-btn"
                                            onClick={() => {
                                                handleInputChange('partner_type_selections', []);
                                            }}
                                        >
                                            Deselect All
                                        </button>
                                    </div>
                                    
                                    <div className="crm-modern-multi-select-options">
                                        {partnerTypeOptions.map((option) => (
                                            <div key={option} className="crm-modern-multi-select-option">
                                                <label className="crm-modern-checkbox-container">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.partner_type_selections?.includes(option) || false}
                                                        onChange={() => handleMultiSelectToggle('partner_type_selections', option)}
                                                    />
                                                    <span className="crm-modern-checkbox-checkmark"></span>
                                                    {option}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        {formData.partner_type_selections?.length > 0 && (
                            <div className="crm-selected-tags">
                                {formData.partner_type_selections.map((tag) => (
                                    <div key={tag} className="crm-tag">
                                        {tag}
                                        <button
                                            type="button"
                                            className="crm-tag-remove"
                                            onClick={() => removeTag('partner_type_selections', tag)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="crm-input-group">
                        <label className="crm-input-label required">Industry</label>
                        <div className="crm-modern-multi-select-container" ref={industryRef}>
                            <div 
                                className={`crm-modern-multi-select-header ${showIndustryDropdown ? 'active' : ''}`}
                                onClick={() => setShowIndustryDropdown(!showIndustryDropdown)}
                            >
                                <span>
                                    {formData.industry_selections?.length === 0 
                                        ? 'Select industries' 
                                        : `${formData.industry_selections.length} selected`}
                                </span>
                            </div>
                            
                            {showIndustryDropdown && (
                                <div className="crm-modern-multi-select-dropdown">
                                    <div className="crm-modern-multi-select-actions">
                                        <button 
                                            type="button" 
                                            className="crm-modern-multi-select-action-btn"
                                            onClick={() => {
                                                handleInputChange('industry_selections', industryOptions);
                                            }}
                                        >
                                            Select All
                                        </button>
                                        <button 
                                            type="button" 
                                            className="crm-modern-multi-select-action-btn"
                                            onClick={() => {
                                                handleInputChange('industry_selections', []);
                                            }}
                                        >
                                            Deselect All
                                        </button>
                                    </div>
                                    
                                    <div className="crm-modern-multi-select-options">
                                        {industryOptions.map((option) => (
                                            <div key={option} className="crm-modern-multi-select-option">
                                                <label className="crm-modern-checkbox-container">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.industry_selections?.includes(option) || false}
                                                        onChange={() => handleMultiSelectToggle('industry_selections', option)}
                                                    />
                                                    <span className="crm-modern-checkbox-checkmark"></span>
                                                    {option}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        {formData.industry_selections?.length > 0 && (
                            <div className="crm-selected-tags">
                                {formData.industry_selections.map((tag) => (
                                    <div key={tag} className="crm-tag">
                                        {tag}
                                        <button
                                            type="button"
                                            className="crm-tag-remove"
                                            onClick={() => removeTag('industry_selections', tag)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Partner Sub-Category Multi-Select - Full Width */}
                <div className="crm-input-group">
                    <label className="crm-input-label">Partner Sub-Category</label>
                    <div className="crm-modern-multi-select-container" ref={subcategoryRef}>
                        <div 
                            className={`crm-modern-multi-select-header ${showSubcategoryDropdown ? 'active' : ''}`}
                            onClick={() => setShowSubcategoryDropdown(!showSubcategoryDropdown)}
                        >
                            <span>
                                {formData.partner_subcategory_selections?.length === 0 
                                    ? 'Select sub-categories' 
                                    : `${formData.partner_subcategory_selections.length} selected`}
                            </span>
                        </div>
                        
                        {showSubcategoryDropdown && (
                            <div className="crm-modern-multi-select-dropdown">
                                <div className="crm-modern-multi-select-actions">
                                    <button 
                                        type="button" 
                                        className="crm-modern-multi-select-action-btn"
                                        onClick={() => {
                                            handleInputChange('partner_subcategory_selections', subcategoryOptions);
                                        }}
                                    >
                                        Select All
                                    </button>
                                    <button 
                                        type="button" 
                                        className="crm-modern-multi-select-action-btn"
                                        onClick={() => {
                                            handleInputChange('partner_subcategory_selections', []);
                                        }}
                                    >
                                        Deselect All
                                    </button>
                                </div>
                                
                                <div className="crm-modern-multi-select-options">
                                    {subcategoryOptions.map((option) => (
                                        <div key={option} className="crm-modern-multi-select-option">
                                            <label className="crm-modern-checkbox-container">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.partner_subcategory_selections?.includes(option) || false}
                                                    onChange={() => handleMultiSelectToggle('partner_subcategory_selections', option)}
                                                />
                                                <span className="crm-modern-checkbox-checkmark"></span>
                                                {option}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    {formData.partner_subcategory_selections?.length > 0 && (
                        <div className="crm-selected-tags">
                            {formData.partner_subcategory_selections.map((tag) => (
                                <div key={tag} className="crm-tag">
                                    {tag}
                                    <button
                                        type="button"
                                        className="crm-tag-remove"
                                        onClick={() => removeTag('partner_subcategory_selections', tag)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Company Website & Incorporation Date - Side by Side */}
                <div className="crm-input-row">
                    <div className="crm-input-group half-width">
                        <label className="crm-input-label">Company Website</label>
                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                            <input
                                type="url"
                                className="crm-input"
                                value={formData.company_website_url}
                                onChange={(e) => handleInputChange('company_website_url', e.target.value)}
                                placeholder="https://www.company.com"
                                style={{ flex: 1 }}
                            />
                            {formData.company_website_url && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        
                                        let url = formData.company_website_url.trim();
                                        
                                        // Remove any leading/trailing whitespace and validate
                                        if (!url) {
                                            console.warn('No URL provided');
                                            return;
                                        }
                                        
                                        // Add https:// if no protocol is specified
                                        if (!url.startsWith('http://') && !url.startsWith('https://')) {
                                            url = 'https://' + url;
                                        }
                                        
                                        console.log('Opening URL:', url);
                                        
                                        // Create a temporary link and click it - this bypasses popup blockers
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.target = '_blank';
                                        link.rel = 'noopener noreferrer';
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                    }}
                                    style={{
                                        padding: '0.625rem 1rem',
                                        fontSize: '0.875rem',
                                        background: 'linear-gradient(135deg, #ffc20c 0%, #e68a00 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        boxShadow: '0 2px 8px rgba(255, 153, 0, 0.3)',
                                        transition: 'all 0.3s ease',
                                        fontWeight: '600'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 153, 0, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 153, 0, 0.3)';
                                    }}
                                    title="Visit website in new tab"
                                >
                                    <span>Visit</span>
                                </button>
                            )}
                        </div>
                    </div>
                    
                    <div className="crm-input-group half-width">
                        <label className="crm-input-label">Incorporation Date</label>
                        <DatePicker
                            name="incorporation_date"
                            value={formData.incorporation_date || ''}
                            onChange={(e) => handleInputChange('incorporation_date', e.target.value)}
                            className="crm-input"
                            format="YYYY-MM-DD"
                            placeholder="Select incorporation date"
                        />
                    </div>
                </div>
            </div>

            <div className="crm-form-section">
                <h2 className="crm-section-title">Address Information</h2>
                
                {/* Head Office Address */}
                <div className="crm-input-row" style={{ paddingBottom: '1.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
                    <div className="crm-input-group full-width">
                        <label className="crm-input-label required">Head Office Address</label>
                        <AddressInput
                            name="head_office_address"
                            value={formData.head_office_address_data?.formatted_address || ''}
                            onChange={(e) => handleInputChange('head_office_address_data', {
                                ...formData.head_office_address_data,
                                formatted_address: e.target.value
                            })}
                            onSelect={(suggestion) => handleAddressSelect('head_office_address_data', suggestion)}
                            placeholder="Start typing head office address..."
                            className="crm-input"
                            styleType="crm"
                            rows={2}
                        />
                    </div>
                </div>

                {/* Invoice Address */}
                <div className="crm-input-row" style={{ paddingBottom: '1.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
                    <div className="crm-input-group full-width">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                            <label className="crm-input-label">Invoice Address</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <label htmlFor="same-as-head-office" className="crm-checkbox-label" style={{ margin: 0, fontSize: '0.875rem' }}>
                                    Same as Head Office
                                </label>
                                <input
                                    type="checkbox"
                                    id="same-as-head-office"
                                    className="crm-checkbox"
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            handleInputChange('invoice_address_data', {
                                                ...formData.head_office_address_data
                                            });
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        <AddressInput
                            name="invoice_address"
                            value={formData.invoice_address_data?.formatted_address || ''}
                            onChange={(e) => handleInputChange('invoice_address_data', {
                                ...formData.invoice_address_data,
                                formatted_address: e.target.value
                            })}
                            onSelect={(suggestion) => handleAddressSelect('invoice_address_data', suggestion)}
                            placeholder="Start typing invoice address (leave blank to use head office)..."
                            className="crm-input"
                            styleType="crm"
                            rows={2}
                        />
                        
                    </div>
                </div>

                {/* Sites and Other Addresses (CRMPartnerOperatingLocation: mines, terminals,
                    sites; picked as Pit to Port locations on a delivery) */}
                <div className="crm-input-row">
                    <div className="crm-input-group full-width">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <label className="crm-input-label">Sites and Other Addresses</label>
                            <button
                                type="button"
                                className="crm-btn-secondary"
                                onClick={addBankAccountHolderAddress}
                                style={{
                                    padding: '0.5rem 1rem',
                                    fontSize: '0.875rem',
                                    backgroundColor: '#4472C4',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    cursor: 'pointer'
                                }}
                            >
                                + Add Address
                            </button>
                        </div>
                        
                        {formData.bank_account_holder_address_data?.length === 0 && (
                            <div style={{ 
                                padding: '2rem', 
                                textAlign: 'center', 
                                color: '#6b7280', 
                                backgroundColor: '#f9fafb',
                                border: '2px dashed #d1d5db',
                                borderRadius: '0.5rem'
                            }}>
                                No sites or other addresses yet. Click "Add Address" to start.
                            </div>
                        )}

                        {formData.bank_account_holder_address_data?.map((location, index) => (
                            <div key={location.id} style={{ 
                                marginBottom: '0.75rem', 
                                padding: '0.75rem', 
                                border: '1px solid #e5e7eb', 
                                borderRadius: '0.5rem',
                                backgroundColor: '#f9fafb'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                    <span style={{ fontWeight: '500', color: '#374151' }}>
                                        Address {index + 1}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => removeBankAccountHolderAddress(location.id)}
                                        style={{
                                            color: '#ef4444',
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '1.25rem',
                                            padding: '0.25rem'
                                        }}
                                        title="Remove address"
                                    >
                                        ×
                                    </button>
                                </div>
                                <AddressInput
                                    name={`bank_account_holder_address_${location.id}`}
                                    value={location.formatted_address || ''}
                                    onChange={(e) => handleBankAccountHolderAddressChange(location.id, e.target.value)}
                                    onSelect={(suggestion) => handleBankAccountHolderAddressSelect(location.id, suggestion)}
                                    placeholder="Start typing the address of a site, mine, terminal or office..."
                                    className="crm-input"
                                    styleType="crm"
                                    rows={2}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* HAMSTIR Partner Profile Builder */}
            <HAMSTIRBuilderBlock 
                profileData={profileData}
                onDataChange={onDataChange}
                isNewProfile={isNewProfile}
                onProfileUpdate={(updatedProfile) => {
                    console.log('🔄 Partner Profile Tab: Received profile update:', updatedProfile);
                    
                    // Update the local form data with all the updated fields
                    setFormData(prev => ({
                        ...prev,
                        ...updatedProfile
                    }));
                    
                    // Call onDataChange for each field to update the global profileData
                    // This will force all tabs to re-render their formData via their useEffect
                    Object.keys(updatedProfile).forEach(field => {
                        if (field !== 'hamstir_update_timestamp') { // Skip the timestamp field
                            console.log(`📤 Updating global field: ${field} =`, updatedProfile[field]);
                            onDataChange(field, updatedProfile[field]);
                        }
                    });
                    
                    // Add a small delay to ensure the global update propagates
                    setTimeout(() => {
                        console.log('✅ Profile update propagation complete');
                    }, 100);
                }}
            />
        </div>
    );
};

export default CRMPartnerProfileTab; 